import { Input, Text, InputGroup } from '@chakra-ui/react';
import {
  NON_DIGIT_GLOBAL_REGEX,
  THOUSANDS_SEPARATOR_REGEX,
} from '@/constants/regex';
import { useField } from 'formik';
import { useRef } from 'react';
import { colors } from '@/theme/foundations/colors';
import { currencyFormatterHelpers } from '@/utils/currencyFormatter';

const { formatCurrencyNumber, cleanCurrencyInput } = currencyFormatterHelpers;

interface PlainInputProps {
  item?: Record<string, unknown>;
  field: any;
  formik?: any;
}

export const PlainInput = ({ item = {}, field, formik }: PlainInputProps) => {
  const {
    name,
    inputType,
    isEditable = true,
    placeholder = '',
    label,
    styles,
    maxLength,
    disabled,
    inputRef,
    formatType,
    currencyFieldName,
    minValue = 0,
    maxValue,
    isInputSubmit = false,
    customValidation,
    showErrorStyles = false,
    isRequired,
  } = item as any;

  const [, meta, helpers] = useField(name);
  const { setTouched, setValue } = helpers || {};
  const { submitCount } = formik || {};
  const { value, touched } = meta || {};

  const [, currencyMeta] = useField(
    currencyFieldName || 'dummy_currency_field'
  );
  const selectedCurrencyId = currencyFieldName ? currencyMeta?.value : null;

  const localInputRef = useRef<HTMLInputElement | null>(null);
  if (!item) return null;

  const showError =
    (submitCount > 0 || touched) &&
    (!(value?.toString()?.length > 0) || meta?.error) &&
    !showErrorStyles &&
    isRequired;

  const isDisabled = !!(disabled || !isEditable);

  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    const numericValue = value
      ?.split('.')[0]
      .replace(NON_DIGIT_GLOBAL_REGEX, '');
    return numericValue?.replace(THOUSANDS_SEPARATOR_REGEX, ',');
  };

  const getSelectedCurrencyString = () => {
    const currencies = (
      item as { currencies?: Array<{ id: unknown; currency?: string }> }
    ).currencies;
    if (!currencyFieldName || !selectedCurrencyId || !currencies) return null;

    const selectedCurrency = currencies.find(
      (c) => c.id === selectedCurrencyId
    );
    return selectedCurrency?.currency || null;
  };

  const formattedValue = (() => {
    // Use `??` so the number 0 isn't coerced to '' and erased from the input.
    const rawValue = field?.value ?? '';
    if (formatType === 'currency') {
      const currencyString = getSelectedCurrencyString();
      if (currencyString && currencyFieldName) {
        return formatCurrencyNumber(rawValue, currencyString);
      }
      return formatNumberWithCommas(rawValue);
    }
    return rawValue;
  })();

  const handleOnChange = (event) => {
    const val = event.target.value;

    if (customValidation) {
      customValidation(val, event, field?.onChange);
    } else if (formatType !== 'currency') {
      field?.onChange(event);
    } else {
      const cleanedValue = cleanCurrencyInput(val);
      setValue(cleanedValue);
    }
  };

  return (
    <InputGroup
      w="100%"
      minH="44px"
      alignItems="center"
      transition="background-color 0.15s ease, border-color 0.15s ease"
      bg={
        showError
          ? 'pastel-1-light'
          : isDisabled
            ? 'secondary-25'
            : 'base-white'
      }
      _hover={{
        borderColor: showError ? 'status-1' : 'secondary-300',
        bg: showError
          ? 'pastel-1-light'
          : isDisabled
            ? 'secondary-25'
            : 'secondary-25',
        boxShadow: 'none',
      }}
      _focusWithin={{
        borderColor: showError ? 'status-1' : 'secondary-300',
        bg: showError ? 'pastel-1-light' : 'secondary-25',
        boxShadow: 'none',
      }}
      border="1px solid"
      borderColor={showError ? 'status-1' : 'secondary-100'}
      borderRadius="6px"
    >
      <Input
        {...field}
        onChange={handleOnChange}
        onClick={() => {
          if (inputRef?.current) {
            inputRef.current?.focus();
          } else if (localInputRef?.current) {
            localInputRef.current?.focus();
          }
        }}
        ref={inputRef || localInputRef}
        id={name}
        variant="outline"
        autoComplete="off"
        _placeholder={{
          opacity: 1,
          fontSize: '12px',
          color: 'secondary-500',
        }}
        _hover={{
          outline: 'none !important',
          border: 'none!important',
          boxShadow: 'none !important',
        }}
        _focus={{
          outline: 'none !important',
          border: 'none !important',
          boxShadow: 'none !important',
        }}
        type={inputType || 'text'}
        disabled={disabled || !isEditable}
        value={formattedValue}
        placeholder={placeholder || `Enter ${label?.toLowerCase()}`}
        maxLength={maxLength}
        min={minValue}
        max={maxValue}
        onFocus={() => setTouched && setTouched(false)}
        onBlur={() => setTouched && setTouched(true)}
        background="transparent"
        color={showError ? 'status-1' : colors['secondary-700']}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (formik && isInputSubmit) {
              formik.submitForm();
            }
          }
        }}
        {...styles}
      />
      {!!maxLength && (
        <Text pt="13px" color="secondary-500" size="xs-medium" pr="5px">
          {String(field?.value ?? '').length}/{maxLength}
        </Text>
      )}
    </InputGroup>
  );
};
